import {
  Button,
  CircularProgress,
  IconButton,
  Tooltip,
  Typography,
} from '@material-ui/core'
import {
  CheckOutlined,
  ErrorOutlineOutlined,
  TimerOutlined,
} from '@material-ui/icons'
import { useIsBuyingPartyView } from 'components/Common/Portal/useIsBuyerPartyView'
import { isEqual } from 'lodash'
import { useTranslation } from 'react-i18next'
import { useAppSelector } from 'store/configureStore'
import { useAppDispatch } from 'store/configureStore'
import { toggleManufacturabilityFlag } from '../store/asyncActions/toggleManufacturabilityFlag'
import { bomItemManufacturabilitySelector } from '../store/selectors/bomItemManufacturabilitySelector'
import { projectOperationIsPendingSelector } from '../store/selectors/projectOperationPendingSelector'
import { CSSProperties, useCallback } from 'react'

export const BomItemManufacturableButton = ({
  bomItemId,
  iconOnly = false,
  buttonStyles = undefined,
  hideIsManufacturableState = undefined,
}: {
  bomItemId: string
  iconOnly?: boolean
  buttonStyles?: CSSProperties
  hideIsManufacturableState?: boolean
}) => {
  const { t } = useTranslation()
  const manufacturabilityStatus = useAppSelector(
    bomItemManufacturabilitySelector(bomItemId),
    isEqual
  )
  const operationIsPending = useAppSelector(
    projectOperationIsPendingSelector(
      toggleManufacturabilityFlag.typePrefix.concat('/', bomItemId)
    )
  )

  const dispatch = useAppDispatch()

  const handleClick = () => {
    if (isBuyer) return
    dispatch(toggleManufacturabilityFlag({ bomItemId }))
  }

  const ButtonText = useCallback(() => {
    let text = ''

    if (
      manufacturabilityStatus.manufacturableStatus === 'Done' ||
      manufacturabilityStatus.manufacturableStatus === 'Overridden'
    ) {
      if (manufacturabilityStatus.isManufacturable) {
        text = t('project:manufacturable-success', 'manufacturable')
      } else {
        text = t('project:manufacturable-failure', 'not manufacturable')
      }
    }

    if (manufacturabilityStatus.manufacturableStatus === 'Requested') {
      text = t('project:manufacturable-requested', 'waiting...')
    }

    if (manufacturabilityStatus.manufacturableStatus === 'Running') {
      text = t('project:manufacturable-running', 'running...')
    }

    return <Typography variant="body2">{text}</Typography>
  }, [
    manufacturabilityStatus.isManufacturable,
    manufacturabilityStatus.manufacturableStatus,
    t,
  ])

  const ButtonTooltip = useCallback(() => {
    if (manufacturabilityStatus?.manufacturableResult) {
      return (
        <Typography variant="body2">
          {manufacturabilityStatus.manufacturableResult}
        </Typography>
      )
    }

    return null
  }, [manufacturabilityStatus.manufacturableResult])

  const isBuyer = useIsBuyingPartyView()

  if (
    !manufacturabilityStatus ||
    manufacturabilityStatus.manufacturableStatus === 'NotApplicable'
  ) {
    return null
  }

  const ButtonIcon = () => {
    if (operationIsPending) {
      return <CircularProgress size={'1em'} />
    }

    if (
      manufacturabilityStatus.manufacturableStatus === 'Done' ||
      manufacturabilityStatus.manufacturableStatus === 'Overridden'
    ) {
      if (manufacturabilityStatus.isManufacturable) {
        return <CheckOutlined />
      } else {
        return <ErrorOutlineOutlined />
      }
    }

    if (
      manufacturabilityStatus.manufacturableStatus === 'Requested' ||
      manufacturabilityStatus.manufacturableStatus === 'Running'
    ) {
      return <TimerOutlined />
    }

    return <TimerOutlined />
  }

  if (hideIsManufacturableState && manufacturabilityStatus.isManufacturable) {
    return null
  }

  return (
    <Tooltip
      title={
        manufacturabilityStatus?.manufacturableResult ? (
          <ButtonTooltip />
        ) : iconOnly ? (
          <ButtonText />
        ) : (
          ''
        )
      }
      arrow
    >
      <div>
        {iconOnly ? (
          <span>
            <IconButton
              onClick={handleClick}
              size="small"
              style={{
                ...(buttonStyles || {}),
                pointerEvents: isBuyer ? 'none' : 'auto',
              }}
            >
              <ButtonIcon />
            </IconButton>
          </span>
        ) : (
          <Button
            size="small"
            variant="text"
            startIcon={<ButtonIcon />}
            onClick={handleClick}
            style={{
              pointerEvents: isBuyer ? 'none' : 'auto',
            }}
          >
            {!iconOnly && (
              <div
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'flex-start',
                }}
              >
                <ButtonText />
              </div>
            )}
          </Button>
        )}
      </div>
    </Tooltip>
  )
}
